import type { HTMLAttributes } from 'react';

import { cn } from '../utils/cn';

export type BadgeVariant =
  | 'best'
  | 'cheapest'
  | 'fastest'
  | 'slow'
  | 'highlight'
  | 'default';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

// Tinted variants (`cheapest`, `fastest`, `slow`) need an OPAQUE background
// because the badge sits as a ribbon on top of the chip's border line — a
// translucent fill (`bg-X/15`) lets that 1px border bleed through and
// visually cuts the pill in half. We pre-mix the brand color with the
// elevated surface token via `color-mix` so the resulting flat color looks
// identical to the previous 15% wash but fully covers what's underneath.
// Border stays slightly translucent on purpose: it sits on top of the now
// opaque fill, so there's nothing to bleed through.
const variantClasses: Record<BadgeVariant, string> = {
  // `best` is fully filled with the brand accent — visually the loudest pill,
  // matching its editorial weight ("we recommend this").
  best: 'bg-accent text-accent-fg border-transparent',
  cheapest:
    'bg-[color-mix(in_srgb,var(--color-success)_15%,var(--color-bg-elevated))] text-success border-success/30',
  fastest:
    'bg-[color-mix(in_srgb,var(--color-accent)_15%,var(--color-bg-elevated))] text-accent border-accent/30',
  slow: 'bg-[color-mix(in_srgb,var(--color-warning)_15%,var(--color-bg-elevated))] text-warning border-warning/30',
  // Partner-tunable lime — kept fully filled so it pops on white surfaces
  // (the lime swatch is intentionally bright). Foreground is the dark
  // brand ink derived token so it works regardless of partner override.
  highlight: 'bg-highlight text-highlight-fg border-transparent',
  default: 'bg-bg-muted text-fg border-border',
};

/**
 * Compact pill used to flag chips with editorial / factual labels
 * (Best, Cheapest, Fastest, Slow). Rendered as a ribbon sitting on the
 * chip's top border by `Chip` — see the `badge` prop there for placement
 * details and the headroom callers must reserve above the grid.
 */
export const Badge = ({
  variant = 'default',
  className,
  children,
  ...props
}: BadgeProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide',
      variantClasses[variant],
      className,
    )}
    {...props}
  >
    {children}
  </span>
);
